/* eslint-disable camelcase */
import { useTranslation } from 'react-i18next';
import { Association } from 'src/shared/models/association/association';
import { District } from 'src/shared/models/district/district';
import * as yup from 'yup';

const emptyToNull = (value: any, originalValue: any) => (originalValue === '' ? null : value);

export const useSchemaEvent = () => {
  const { t } = useTranslation();
  const requiredMessage = t('common:validationMessages.required');

  const eventRequestPostSchema = yup
    .object({
      // UI toggle: hook this to your switch/checkbox
      useTickets: yup.boolean().default(false).required(),
      locationName: yup
        .string()
        .required(requiredMessage)
        .max(250, t('common:validationMessages.maxLength', { count: 250 })),
      name: yup
        .string()
        .required(requiredMessage)
        .max(100, t('common:validationMessages.maxLength', { count: 100 })),
      description: yup.string().optional().nullable(),
      date: yup.date().required(requiredMessage).nullable(),
      // If you also gate on "owner_id", keep that logic inside the branches
      owner_id: yup.number().optional(),

      // --- Fee path (when useTickets = false) ---
      fee: yup
        .number()
        .transform(emptyToNull)
        .when(['useTickets', 'owner_id'], {
          is: (useTickets: boolean, owner_id?: number) => !useTickets && !!owner_id,
          then: (s) => s.required(requiredMessage),
          otherwise: (s) => s.strip(), // drop from payload when using tickets
        }),
      memberFee: yup
        .number()
        .transform(emptyToNull)
        .when(['useTickets', 'owner_id'], {
          is: (useTickets: boolean, owner_id?: number) => !useTickets && !!owner_id,
          then: (s) => s.required(requiredMessage),
          otherwise: (s) => s.strip(),
        }),

      // --- Tickets path (when useTickets = true) ---
      tickets: yup
        .array(
          yup.object({
            ticketName: yup
              .string()
              .required(requiredMessage)
              .max(100, t('common:validationMessages.maxLength', { count: 100 })),
            price: yup.number().transform(emptyToNull).required(requiredMessage),
            memberPrice: yup.number().transform(emptyToNull).required(requiredMessage),
            invoiceArticleId: yup.number().transform(emptyToNull).optional().nullable(),
            createdById: yup.number().transform(emptyToNull).required(),
            enabledAt: yup.string().transform(emptyToNull).nullable(),
          }),
        )
        .when('useTickets', {
          is: true,
          then: (s) => s.min(1, t('event:validationMessage.atLeastOneTicket')).required(requiredMessage),
          otherwise: (s) => s.strip(), // drop tickets when flat fee is used
        }),

      minParticipants: yup.number().required(requiredMessage).transform(emptyToNull),
      maxParticipants: yup.number().required(requiredMessage).transform(emptyToNull),

      address: yup.string().required(requiredMessage),
      postcode: yup.string().required(requiredMessage),
      city: yup.string().required(requiredMessage),
      lastDateForApplication: yup
        .date()
        .required()
        .nullable()
        .transform((v) => (v instanceof Date && !Number.isNaN(v) ? v : null))
        .typeError(t('common:validationMessages.fsDateFormat'))
        .max(yup.ref('date'), t('event:validationMessage.eventDateMustBeBefore')),

      contactPersonName: yup.string().optional().nullable(),
      contactPersonEmail: yup.string().optional().nullable(),
      contactPersonPhone: yup.string().optional().nullable(),
      allergy: yup.boolean().required(requiredMessage),
      publicSignUp: yup.boolean().required(requiredMessage),
      membersOnly: yup.boolean().required(requiredMessage),
      association: yup.mixed<Association>().optional().nullable(),
      district: yup.mixed<District>().optional().nullable(),
      files: yup.mixed<File[]>().optional().nullable().notRequired(),
    })
    // Optional: enforce XOR (either flat fee or tickets) at the object level
    .test('fee-xor-tickets', t('event:validationMessage.feeOrTickets'), (value) => {
      if (!value) return false;
      const usingTickets = !!value.useTickets;
      if (usingTickets) return Array.isArray(value.tickets) && value.tickets.length > 0;
      // if not using tickets, consider fee required only when owner_id is present (your original rule)
      if (value.owner_id) return value.fee != null && value.memberFee != null;
      return true;
    });

  return eventRequestPostSchema;
};
